/* eslint-disable prefer-destructuring */
import lod_ from "lodash";
import { Divider, Icon, IconButton, Tooltip } from "@mui/material";
import { t } from "i18next";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { useState } from "react";
import DatePresets from "components/Custom/DatePresets";
import DictionaryMenu from "pages/settings/filters/DictionaryMenu";
import { getLabelPath, getLabel } from "helpers/dictionary";
import { useSelector } from "react-redux";

const DefaultDateValue = ({ value, onEdit = () => {} }) => {
	const [defaultValue, setDefaultValue] = useState(value);

	return (
		<MDBox display="flex" alignItems="center" justifyContent="space-between">
			<MDBox
				mr={1}
				style={{
					whiteSpace: "nowrap"
				}}
			>
				<MDTypography variant="caption">{t("FILTERS.date.PRESETS.defaultValue")}</MDTypography>
			</MDBox>
			<MDBox
				flex="1"
				style={{
					border: "1px solid #ccc",
					borderRadius: "4px",
					padding: "4px",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%"
				}}
			>
				<MDTypography fontSize="small" variant="caption">
					{defaultValue
						? t(`FILTERS.date.PRESETS.${defaultValue}`)
						: t("SETTINGS.CHARTS.NEW.noSelection")}
				</MDTypography>
				<IconButton
					size="small"
					onClick={() => {
						setDefaultValue("");
						onEdit({
							defaultValue: null
						});
					}}
				>
					<Icon>close</Icon>
				</IconButton>
			</MDBox>
			<MDBox ml={1}>
				<DatePresets
					fullHeight
					handleSelectPreset={({ interval, code }) => {
						setDefaultValue(code);
						onEdit({
							defaultValue: code
						});
					}}
				/>
			</MDBox>
		</MDBox>
	);
};

const SpecialFilterItem = ({ item, index, onEdit = () => {}, deleteItem = () => {} }) => {
	return (
		<MDBox display="flex" flexDirection="column" flex="1">
			<MDBox display="flex" alignItems="center" justifyContent="space-between">
				<MDTypography variant="subtitle2" fontSize="small">
					{t("FILTERS.basicFieldTitle")}
				</MDTypography>
				<IconButton size="small" onClick={() => deleteItem(index)}>
					<Icon>delete</Icon>
				</IconButton>
			</MDBox>
			<MDTypography variant="h6">{t(`FILTERS.${item.path}`)}</MDTypography>
			{item.type.includes("date") && (
				<>
					<Divider />
					<DefaultDateValue value={item.defaultValue} onEdit={onEdit} />
				</>
			)}
		</MDBox>
	);
};

const DictionaryFilterItem = ({
	item,
	index,
	dictionary: dictionaries,
	deleteItem = () => {},
	language,
	//
	value = "",
	linkItem,
	removeLink,
	onEdit
}) => {
	const codeDictionary = item.dictionary;
	const path = item.path;
	const fullItemPath = `${codeDictionary}.${path}`;

	const dictionary = dictionaries[codeDictionary];

	const dictionaryItem = lod_.get(dictionary?.items ?? {}, path.replaceAll(".", ".items."), {});

	const [dictionaryAnchorEl, setDictionaryAnchorEl] = useState(null);

	return (
		<MDBox>
			<MDBox display="flex" flexDirection="column">
				<MDBox display="flex" alignItems="center" justifyContent="space-between" flex="1">
					{/*  */}
					<MDTypography variant="subtitle2" fontSize="small">
						{getLabelPath(dictionaries, fullItemPath)}
					</MDTypography>
					{/*  */}
					<MDBox
						style={{
							whiteSpace: "nowrap"
						}}
					>
						{/* Link */}
						<Tooltip title={t("SETTINGS.FILTERS.linkAttribute")} placement="top" arrow>
							<IconButton
								size="small"
								onClick={e => {
									setDictionaryAnchorEl(e.target);
								}}
							>
								<Icon>link</Icon>
							</IconButton>
						</Tooltip>
						<DictionaryMenu
							dictionary={dictionaries}
							anchorEl={dictionaryAnchorEl}
							handleInsertText={e => {
								if (e !== fullItemPath) {
									linkItem(item, index, e);
								}
								setDictionaryAnchorEl(null);
							}}
							handleClose={() => setDictionaryAnchorEl(null)}
						/>
						{/* Delete */}
						<Tooltip title={t("SETTINGS.FILTERS.deleteFilter")} placement="top" arrow>
							<IconButton
								size="small"
								onClick={() => {
									deleteItem(index);
								}}
							>
								<Icon>delete</Icon>
							</IconButton>
						</Tooltip>
					</MDBox>
				</MDBox>
				{/*  */}
				<MDTypography variant="h6">{getLabel(dictionaryItem.label, language)}</MDTypography>
			</MDBox>
			{dictionaryItem.type.includes("date") && (
				<>
					<Divider />
					<DefaultDateValue value={value} onEdit={onEdit} />
				</>
			)}
			{item.links?.length > 0 && (
				<MDBox>
					<Divider />
					<MDTypography variant="caption">{t("FILTERS.linkedFields")}</MDTypography>
					{(item.links ?? []).map((link, linkIndex) => {
						return (
							<MDBox
								key={linkIndex}
								display="flex"
								flexDirection="row"
								alignItems="center"
								justifyContent="space-between"
								className="link-row-filter"
								p={0.5}
								borderRadius="md"
							>
								<MDTypography
									variant="body2"
									fontSize="small"
									style={{
										fontWeight: "bold"
									}}
								>
									{getLabelPath(dictionaries, `${link.dictionary}.${link.path}`)}
								</MDTypography>
								<Tooltip title={t("SETTINGS.FILTERS.deleteLink")} placement="top" arrow>
									<IconButton size="small" onClick={() => removeLink(index, linkIndex)}>
										<Icon>delete</Icon>
									</IconButton>
								</Tooltip>
							</MDBox>
						);
					})}
				</MDBox>
			)}
		</MDBox>
	);
};

/**
 * Template part for one message in the middle pannel
 * @param {*} param0
 * @returns
 */
const FilterItemDndList = ({
	item,
	index,
	dictionary,
	deleteItem = () => {},
	linkItem,
	removeLink,
	onEdit
}) => {
	const user = useSelector(state => state.user);
	const language = user.language;

	const source = item.source;

	if (source === "special") {
		return (
			<SpecialFilterItem
				item={item}
				index={index}
				deleteItem={deleteItem}
				onEdit={edit => onEdit(index, edit)}
			/>
		);
	} else {
		return (
			<DictionaryFilterItem
				item={item}
				index={index}
				dictionary={dictionary}
				deleteItem={deleteItem}
				language={language}
				linkItem={linkItem}
				removeLink={removeLink}
				onEdit={edit => onEdit(index, edit)}
			/>
		);
	}
};

export default FilterItemDndList;
